import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { subscriptionPlanApi } from '@/services/subscriptionApi';
import { useToast } from '@/hooks/useToast';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { ArrowLeft, CreditCard, BarChart3 } from 'lucide-react';
import type { PlanType, PlanStatus } from '@/types/subscription';

export const SubscriptionPlanStatsPage: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    draft: 0,
    inactive: 0,
    archived: 0,
    trialEnabled: 0,
    byType: {} as Record<PlanType, number>,
    byStatus: {} as Record<PlanStatus, number>,
    averageMonthly: 0,
    averageYearly: 0,
    byCountry: {} as Record<string, number>,
    byGateway: {} as Record<string, number>,
  });
  const { toast } = useToast();

  useEffect(() => {
    fetchStats();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount
  }, []);

  const fetchStats = async () => {
    try {
      setIsLoading(true);
      // Fetch all plans (with a large page size to get all)
      const response = await subscriptionPlanApi.list({
        page: 0,
        size: 1000, // Large size to get all plans
      });

      const plans = response.content || [];

      // Calculate statistics
      const newStats = {
        total: plans.length,
        active: plans.filter((p) => p.status === 'ACTIVE').length,
        draft: plans.filter((p) => p.status === 'DRAFT').length,
        inactive: plans.filter((p) => p.status === 'INACTIVE').length,
        archived: plans.filter((p) => p.status === 'ARCHIVED').length,
        trialEnabled: plans.filter((p) => p.trialEnabled).length,
        byType: {} as Record<PlanType, number>,
        byStatus: {} as Record<PlanStatus, number>,
        averageMonthly: 0,
        averageYearly: 0,
        byCountry: {} as Record<string, number>,
        byGateway: {} as Record<string, number>,
      };

      // Count by type
      plans.forEach((plan) => {
        newStats.byType[plan.planType] = (newStats.byType[plan.planType] || 0) + 1;
      });

      // Count by status
      plans.forEach((plan) => {
        newStats.byStatus[plan.status] = (newStats.byStatus[plan.status] || 0) + 1;
      });

      // Calculate averages
      if (plans.length > 0) {
        const totalMonthly = plans.reduce((sum, p) => sum + p.monthlyPrice, 0);
        const totalYearly = plans.reduce((sum, p) => sum + p.yearlyPrice, 0);
        newStats.averageMonthly = totalMonthly / plans.length;
        newStats.averageYearly = totalYearly / plans.length;
      }

      // Count by country
      plans.forEach((plan) => {
        plan.supportedCountries?.forEach((country) => {
          newStats.byCountry[country] = (newStats.byCountry[country] || 0) + 1;
        });
      });

      // Count by gateway
      plans.forEach((plan) => {
        plan.supportedPaymentGateways?.forEach((gateway) => {
          newStats.byGateway[gateway] = (newStats.byGateway[gateway] || 0) + 1;
        });
      });

      setStats(newStats);
    } catch (error: unknown) {
      console.error('Error fetching stats:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: (error as Error)?.message ?? 'Failed to load statistics',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(price);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/admin/subscription-plans">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
              <BarChart3 className="h-8 w-8" />
              Plan Statistics
            </h1>
            <p className="text-muted-foreground mt-2">Overview of subscription plans</p>
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="text-center py-8">Loading statistics...</div>
      ) : (
        <>
          {/* Overview Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-6">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Total Plans</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.total}</div>
                <p className="text-xs text-muted-foreground">All plans</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Active</CardTitle>
                <CreditCard className="h-4 w-4 text-green-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                <p className="text-xs text-muted-foreground">Currently active</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Draft</CardTitle>
                <CreditCard className="h-4 w-4 text-yellow-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-yellow-600">{stats.draft}</div>
                <p className="text-xs text-muted-foreground">In draft</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Trial Enabled</CardTitle>
                <CreditCard className="h-4 w-4 text-blue-600" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-blue-600">{stats.trialEnabled}</div>
                <p className="text-xs text-muted-foreground">With trial</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Monthly</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPrice(stats.averageMonthly)}</div>
                <p className="text-xs text-muted-foreground">Average price</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Avg Yearly</CardTitle>
                <CreditCard className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{formatPrice(stats.averageYearly)}</div>
                <p className="text-xs text-muted-foreground">Average price</p>
              </CardContent>
            </Card>
          </div>

          {/* Distribution by Type */}
          <Card>
            <CardHeader>
              <CardTitle>Distribution by Plan Type</CardTitle>
              <CardDescription>Number of plans per type</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.byType).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.byType)
                    .sort(([, a], [, b]) => b - a)
                    .map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{type}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No plans found</div>
              )}
            </CardContent>
          </Card>

          {/* Distribution by Status */}
          <Card>
            <CardHeader>
              <CardTitle>Distribution by Status</CardTitle>
              <CardDescription>Number of plans per status</CardDescription>
            </CardHeader>
            <CardContent>
              {Object.keys(stats.byStatus).length > 0 ? (
                <div className="space-y-4">
                  {Object.entries(stats.byStatus)
                    .sort(([, a], [, b]) => b - a)
                    .map(([status, count]) => (
                      <div key={status} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{status}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">No plans found</div>
              )}
            </CardContent>
          </Card>

          {/* Distribution by Country */}
          {Object.keys(stats.byCountry).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Distribution by Country</CardTitle>
                <CardDescription>Number of plans supporting each country</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(stats.byCountry)
                    .sort(([, a], [, b]) => b - a)
                    .slice(0, 10)
                    .map(([country, count]) => (
                      <div key={country} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-medium">{country}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Distribution by Payment Gateway */}
          {Object.keys(stats.byGateway).length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>Distribution by Payment Gateway</CardTitle>
                <CardDescription>Number of plans supporting each gateway</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {Object.entries(stats.byGateway)
                    .sort(([, a], [, b]) => b - a)
                    .map(([gateway, count]) => (
                      <div key={gateway} className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{gateway.replace(/_/g, ' ')}</span>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="w-32 bg-secondary rounded-full h-2">
                            <div
                              className="bg-primary h-2 rounded-full"
                              style={{
                                width: `${(count / stats.total) * 100}%`,
                              }}
                            />
                          </div>
                          <span className="text-sm font-medium w-12 text-right">{count}</span>
                        </div>
                      </div>
                    ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Subscription Metrics Placeholder */}
          <Card>
            <CardHeader>
              <CardTitle>Subscription Metrics</CardTitle>
              <CardDescription>Active subscriptions and revenue by plan</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-center py-8 text-muted-foreground">
                <p>Subscription metrics will be available when backend metrics are enabled.</p>
                <p className="text-sm mt-2">
                  This feature requires backend metrics collection to be configured.
                </p>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default SubscriptionPlanStatsPage;
